/**
 * The CRM's own Stripe account, from the browser.
 *
 * A separate client from `lib/billing.ts` and from anything under `sales.ts`,
 * because it talks to a separate account: this one charges OUR clients, the
 * platform's charges Husly's subscribers. Keeping them apart in the browser as
 * well as on the server means a screen cannot accidentally read one account's
 * status while writing the other's keys.
 *
 * NO SECRET EVER COMES BACK. The status shape carries `secretKeySet` booleans
 * and nothing else, which is why the form has to treat an empty field as "leave
 * it alone" rather than as a value to send.
 */
import { api } from "./api";

export type CrmStripeMode = "test" | "live";

export interface CrmStripeEnvStatus {
  secretKeySet: boolean;
  webhookSecretSet: boolean;
}

export interface CrmBillingSettings {
  enabled: boolean;
  mode: CrmStripeMode;
  /** Where the active keys came from. "env" means the database row is empty. */
  source: "db" | "env" | "none";
  /**
   * Which environment the ACTIVE key really belongs to, from its prefix.
   *
   * Worth its own field because the mode switch and the key can disagree - a
   * form saying "live" while holding a test key is a support call nobody can
   * diagnose from outside.
   */
  activeKeyMode: CrmStripeMode | null;
  /** Whether the process can encrypt a new secret at all (`CRM_VAULT_KEY`). */
  vaultReady: boolean;
  vaultReason?: "missing" | "invalid";
  test: CrmStripeEnvStatus;
  live: CrmStripeEnvStatus;
}

export interface CrmBillingPatch {
  enabled?: boolean;
  mode?: CrmStripeMode;
  test?: { secretKey?: string; webhookSecret?: string };
  live?: { secretKey?: string; webhookSecret?: string };
}

/** What asking Stripe who we are came back with. */
export interface CrmStripeTestResult {
  ok: boolean;
  accountId?: string;
  name?: string | null;
  chargesEnabled?: boolean;
  error?: string;
  message?: string;
}

export const getCrmBillingSettings = () =>
  api.get<CrmBillingSettings>("/admin/crm/billing/settings");

export const saveCrmBillingSettings = (patch: CrmBillingPatch) =>
  api.put<CrmBillingSettings>("/admin/crm/billing/settings", patch);

/**
 * Ask Stripe to identify the account.
 *
 * Tests what is SAVED, not what is on screen - unlike the register panel next
 * door, which can pass a typed password straight through. A Stripe secret key is
 * not something to send twice, and the account identity is only meaningful for
 * the key the server will actually charge with.
 */
export const testCrmStripe = () =>
  api.post<CrmStripeTestResult>("/admin/crm/billing/settings/test", {});
